import React from 'react';
import {observer} from 'mobx-react';
import ExchangeLocationsStore from '../../stores/exchange-locations-store';
import {IExchangeLocationRuleRow} from '../../utilities/exchange-locations-table-builder';
import Label, {LABEL_VARIANT} from '../../../../../../components/label/label';
import {ButtonIcon} from '../../../../../../components/inputs/button-icon/button-icon';
import IconEdit from '../../../../../../components/icons/icon-edit/icon-edit';
import IconDelete from '../../../../../../components/icons/icon-delete/icon-delete';
import FontBody3 from '../../../../../../components/fonts/font-body3/font-body3';
import {FONT_COLOR} from '../../../../../../components/fonts/font-props';
import useDashboardTranslation from '../../../../../../dashboard-translation/hooks/dashboard-translation-hook';
import './exchange-locations-table-row.scss';
import Hr from "../../../../../../components/hr/hr";

interface IExchangeLocationsTableRowProps {
    row: IExchangeLocationRuleRow;
    exchangeLocationsStore: ExchangeLocationsStore;
    onEdit: (row: IExchangeLocationRuleRow) => void;
}

/**
 * Single row component for exchange locations table.
 * Displays warehouse labels, zone items, and action buttons.
 */
const ExchangeLocationsTableRow: React.FC<IExchangeLocationsTableRowProps> = observer(
    (
        {
            row,
            exchangeLocationsStore,
            onEdit
        }: IExchangeLocationsTableRowProps
    ) => {
        const {getTranslation} = useDashboardTranslation();
        const pageScope = 'dashboard-settings-store';
        const fallbackRuleText = getTranslation('EXCHANGE_LOCATIONS_TABLE_ROW_FALLBACK_RULE', pageScope);
        const labelFallbackRuleText = getTranslation('EXCHANGE_LOCATIONS_TABLE_ROW_LABEL_FALLBACK_RULE', pageScope);

        /** Handle edit button click */
        const handleEdit = () => {
            onEdit(row);
        };

        /** Handle delete button click */
        const handleDelete = () => {
            exchangeLocationsStore.deleteRule(row);
        };

        return (
            <>
                <div className={`exchange-locations-table-row ${row.is_fallback ? 'exchange-locations-table-row-fallback' : ''}`}>

                    <div className="exchange-locations-table-row-cell">
                        <div className="exchange-locations-table-row-warehouses">
                            {row.warehouse_labels.map((warehouseLabel, index) => (
                                <Label
                                    key={index}
                                    labelText={warehouseLabel}
                                    variant={LABEL_VARIANT.DARK_BLUE}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="exchange-locations-table-row-cell">
                        <div className="exchange-locations-table-row-zones">
                            {
                                row.zone_items.map((zoneItem, index) => (
                                    <Label
                                        key={index}
                                        labelText={row.is_fallback ? labelFallbackRuleText :zoneItem.label}
                                        variant={row.is_fallback ? LABEL_VARIANT.GRAY : LABEL_VARIANT.ACCENT_LIGHT}
                                    />
                                ))
                            }
                        </div>
                    </div>

                    <div className={`exchange-locations-table-row-cell ${row.is_fallback ? 'exchange-locations-table-row-cell-fallback' : ''}`}>
                        <div className="exchange-locations-table-row-actions">
                            {!row.is_fallback && (
                                <ButtonIcon
                                    Component={IconDelete}
                                    onClick={handleDelete}
                                    color="var(--color-accent-1)"
                                />
                            )}

                            {row.is_fallback && (
                                <div className="exchange-locations-table-row-fallback-indicator">
                                    <FontBody3 color={FONT_COLOR.PRIMARY3_50}>
                                        {fallbackRuleText}
                                    </FontBody3>
                                </div>
                            )}

                            <ButtonIcon
                                Component={IconEdit}
                                onClick={handleEdit}
                            />
                        </div>
                    </div>
                </div>
                <Hr/>
            </>
        );
    });

export default ExchangeLocationsTableRow;

